import { RefreshCw } from "lucide-react";
import type { BatchDetailResponse, RuleResponse } from "../../gen/models";
import type { BatchTab } from "./BatchDetailTabsHeader";
import { BatchChangesetTab } from "./BatchChangesetTab";
import { BatchConversationsTab } from "./BatchConversationsTab";
import { BatchCommentsTab, BatchEvaluationsTab } from "./BatchCommentsEvaluationsTabs";

interface BatchDetailTabContentProps {
  loading: boolean;
  batch: BatchDetailResponse | null;
  activeTab: BatchTab;
  changesetSubtab: "rules" | "knowledge" | "guardrails";
  onChangeSubtab: (subtab: "rules" | "knowledge" | "guardrails") => void;
  excludedRuleIds: Set<string>;
  excludedKnowledgeIds: Set<string>;
  excludedGuardrailIds: Set<string>;
  onToggleExcludeRule: (id: string) => void;
  onToggleExcludeKnowledge: (id: string) => void;
  onToggleExcludeGuardrail: (id: string) => void;
  onSaveRule: (
    ruleId: string,
    updates: { title: string; instruction: string; priority: number }
  ) => Promise<void>;
  onSaveKnowledge: (
    itemId: string,
    updates: { title: string; content: string }
  ) => Promise<void>;
  onSaveGuardrail: (
    itemId: string,
    updates: {
      title: string;
      description?: string;
      category: string;
      action: string;
      severity: string;
      default_threshold: number;
      anchors?: Array<{ anchor_type: string; text: string }>;
    }
  ) => Promise<void>;
  baselineRules: Map<string, RuleResponse>;
  onRemoveComment: (id: string) => void;
  onRemoveEvaluation: (id: string) => void;
}

export function BatchDetailTabContent({
  loading,
  batch,
  activeTab,
  changesetSubtab,
  onChangeSubtab,
  excludedRuleIds,
  excludedKnowledgeIds,
  excludedGuardrailIds,
  onToggleExcludeRule,
  onToggleExcludeKnowledge,
  onToggleExcludeGuardrail,
  onSaveRule,
  onSaveKnowledge,
  onSaveGuardrail,
  baselineRules,
  onRemoveComment,
  onRemoveEvaluation,
}: BatchDetailTabContentProps) {
  if (loading) {
    return (
      <div className="p-12 text-center text-xs text-stone-400 flex items-center justify-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
        <span>Loading batch details...</span>
      </div>
    );
  }

  if (!batch) {
    return <div className="p-8 text-center text-xs text-rose-500">Failed to load batch.</div>;
  }

  switch (activeTab) {
    case "changeset":
      return (
        <BatchChangesetTab
          subtab={changesetSubtab}
          onChangeSubtab={onChangeSubtab}
          draftRules={batch.draft_rules || []}
          draftKnowledgeItems={batch.draft_knowledge_items || []}
          draftGuardrails={batch.draft_guardrails || []}
          excludedRuleIds={excludedRuleIds}
          excludedKnowledgeIds={excludedKnowledgeIds}
          excludedGuardrailIds={excludedGuardrailIds}
          onToggleExcludeRule={onToggleExcludeRule}
          onToggleExcludeKnowledge={onToggleExcludeKnowledge}
          onToggleExcludeGuardrail={onToggleExcludeGuardrail}
          onSaveRule={onSaveRule}
          onSaveKnowledge={onSaveKnowledge}
          onSaveGuardrail={onSaveGuardrail}
          baselineRules={baselineRules}
          isCompleted={batch.status === "completed"}
        />
      );
    case "conversations":
      return (
        <BatchConversationsTab
          threads={batch.threads || []}
          comments={batch.comments || []}
          evaluations={batch.evaluations || []}
        />
      );
    case "comments":
      return (
        <BatchCommentsTab
          comments={batch.comments || []}
          onRemoveComment={onRemoveComment}
        />
      );
    case "evaluations":
      return (
        <BatchEvaluationsTab
          evaluations={batch.evaluations || []}
          onRemoveEvaluation={onRemoveEvaluation}
        />
      );
    default:
      return null;
  }
}
